// src/components/ModalDepart.js  — GFM3 RH
// Utilise l'instance axios `api` de services/api.js (token JWT automatique)

import React, { useState, useEffect } from 'react';
import api from '../services/api';

const CATEGORIES_LABELS = {
  fin_contrat:   { label: 'Fin de contrat'    },
  volontaire:    { label: 'Départ volontaire'  },
  disciplinaire: { label: 'Disciplinaire'      },
  autre:         { label: 'Autre'              },
};

export default function ModalDepart({ employe, onClose, onSaved }) {
  const [motifs, setMotifs]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [annulation, setAnnulation] = useState(false);
  const [form, setForm] = useState({
    date_depart:        employe.date_depart        || '',
    motif_depart_id:    employe.motif_depart_id    || '',
    commentaire_depart: employe.commentaire_depart || '',
  });
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Charger les motifs via axios (token JWT automatique)
  useEffect(() => {
    api.get('/motifs-depart')
      .then(res => {
        if (res.data?.success) setMotifs(res.data.data || []);
        else setError('Erreur chargement motifs.');
      })
      .catch(() => setError('Impossible de charger les motifs.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  // Enregistrer → POST /api/employes/{id}/depart
  const handleSave = () => {
    if (!form.date_depart)     return setError('La date de départ est obligatoire.');
    if (!form.motif_depart_id) return setError('Veuillez sélectionner un motif.');

    setSaving(true);
    api.post(`/employes/${employe.id}/depart`, form)
      .then(res => {
        if (res.data?.success) {
          setSuccess(res.data.message || 'Départ enregistré.');
          setTimeout(() => { onSaved && onSaved(); onClose(); }, 1400);
        } else {
          setError(res.data?.message || 'Erreur lors de l\'enregistrement.');
        }
      })
      .catch(err => setError(err?.response?.data?.message || 'Erreur serveur.'))
      .finally(() => setSaving(false));
  };

  // Annuler → DELETE /api/employes/{id}/depart
  const handleAnnuler = () => {
    if (!window.confirm('Annuler le départ ? L\'employé sera remis en statut Actif.')) return;
    setAnnulation(true);
    api.delete(`/employes/${employe.id}/depart`)
      .then(res => {
        if (res.data?.success) {
          setSuccess('Départ annulé. Employé remis Actif.');
          setTimeout(() => { onSaved && onSaved(); onClose(); }, 1400);
        } else {
          setError(res.data?.message || 'Erreur.');
        }
      })
      .catch(err => setError(err?.response?.data?.message || 'Erreur serveur.'))
      .finally(() => setAnnulation(false));
  };

  // Grouper les motifs par catégorie pour les <optgroup>
  const motifsByCategory = motifs.reduce((acc, m) => {
    if (!acc[m.categorie]) acc[m.categorie] = [];
    acc[m.categorie].push(m);
    return acc;
  }, {});

  const isDepart    = form.date_depart && new Date(form.date_depart) <= new Date();
  const statutPrev  = isDepart ? 'Parti(e)' : 'Départ prévu';
  const statutColor = isDepart ? '#ef4444' : '#f59e0b';
  const hasDeparture = ['parti', 'depart_prevu'].includes(employe.statut_emploi);

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div>
            <h2 style={S.title}>
              {hasDeparture ? '✏️ Modifier le départ' : '🚪 Enregistrer un départ'}
            </h2>
            <p style={S.subtitle}>
              {employe.nom_complet} — {employe.matricule} | {employe.type_contrat}
            </p>
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* ── Body ── */}
        <div style={S.body}>

          {loading && (
            <p style={{ color:'#6b7280', textAlign:'center', padding:20 }}>
              Chargement des motifs…
            </p>
          )}

          {!loading && (
            <>
              {/* Statut actuel */}
              <div style={S.infoRow}>
                <span style={S.labelText}>Statut actuel</span>
                <span style={{
                  ...S.badge,
                  background: employe.statut_emploi === 'actif'        ? '#d1fae5'
                            : employe.statut_emploi === 'depart_prevu' ? '#fff7ed'
                            : '#fee2e2',
                  color:      employe.statut_emploi === 'actif'        ? '#065f46'
                            : employe.statut_emploi === 'depart_prevu' ? '#c2410c'
                            : '#991b1b',
                }}>
                  {employe.statut_emploi === 'actif'        ? '● Actif'
                 : employe.statut_emploi === 'parti'        ? '● Parti(e)'
                 : '⚠ Départ prévu'}
                </span>
              </div>

              {/* Date de départ */}
              <div style={S.fieldGroup}>
                <label style={S.labelText}>
                  Date de départ <span style={{ color:'#ef4444' }}>*</span>
                </label>
                <input
                  type="date" name="date_depart" value={form.date_depart}
                  onChange={handleChange} style={S.input}
                />
                {form.date_depart && (
                  <span style={{ fontSize:12, color:statutColor, marginTop:4, display:'block' }}>
                    → Statut prévisible : <strong>{statutPrev}</strong>
                  </span>
                )}
              </div>

              {/* Motif de départ */}
              <div style={S.fieldGroup}>
                <label style={S.labelText}>
                  Motif de départ <span style={{ color:'#ef4444' }}>*</span>
                </label>
                <select
                  name="motif_depart_id" value={form.motif_depart_id}
                  onChange={handleChange} style={S.input}
                >
                  <option value="">— Sélectionner un motif —</option>
                  {Object.entries(motifsByCategory).map(([cat, items]) => (
                    <optgroup key={cat} label={CATEGORIES_LABELS[cat]?.label || cat}>
                      {items.map(m => (
                        <option key={m.id} value={m.id}>{m.libelle}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Commentaire */}
              <div style={S.fieldGroup}>
                <label style={S.labelText}>Commentaire / Observations</label>
                <textarea
                  name="commentaire_depart" value={form.commentaire_depart}
                  onChange={handleChange} rows={3}
                  placeholder="Informations complémentaires sur le départ…"
                  style={{ ...S.input, resize:'vertical' }}
                />
              </div>

              {error   && <div style={S.alertErr}>⚠ {error}</div>}
              {success && <div style={S.alertOk}>✓ {success}</div>}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={S.footer}>
          {hasDeparture && (
            <button onClick={handleAnnuler} disabled={annulation} style={S.btnDanger}>
              {annulation ? 'Annulation…' : '↩ Annuler le départ'}
            </button>
          )}
          <div style={{ flex:1 }} />
          <button onClick={onClose} style={S.btnSecondary}>Fermer</button>
          <button onClick={handleSave} disabled={saving || loading} style={S.btnPrimary}>
            {saving ? 'Enregistrement…' : '✔ Enregistrer le départ'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const S = {
  overlay:    { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
                display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 },
  modal:      { background:'#fff', borderRadius:12, width:560, maxWidth:'95vw',
                maxHeight:'90vh', display:'flex', flexDirection:'column',
                boxShadow:'0 20px 60px rgba(0,0,0,0.3)' },
  header:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                padding:'20px 24px 16px', borderBottom:'1px solid #e5e7eb' },
  title:      { margin:0, fontSize:18, fontWeight:700, color:'#111827' },
  subtitle:   { margin:'4px 0 0', fontSize:13, color:'#6b7280' },
  closeBtn:   { background:'none', border:'none', fontSize:18, cursor:'pointer',
                color:'#9ca3af', padding:'4px 8px', lineHeight:1 },
  body:       { padding:'20px 24px', overflowY:'auto', flex:1 },
  footer:     { padding:'16px 24px', borderTop:'1px solid #e5e7eb',
                display:'flex', gap:10, alignItems:'center' },
  fieldGroup: { marginBottom:18 },
  infoRow:    { display:'flex', justifyContent:'space-between', alignItems:'center',
                marginBottom:16, padding:'10px 14px', background:'#f9fafb',
                borderRadius:8, border:'1px solid #e5e7eb' },
  labelText:  { display:'block', fontSize:13, fontWeight:600,
                color:'#374151', marginBottom:6 },
  badge:      { fontSize:12, fontWeight:700, padding:'3px 12px',
                borderRadius:20, display:'inline-block' },
  input:      { width:'100%', padding:'9px 12px', border:'1px solid #d1d5db',
                borderRadius:8, fontSize:14, outline:'none',
                boxSizing:'border-box', fontFamily:'inherit' },
  alertErr:   { padding:'10px 14px', background:'#fee2e2', color:'#991b1b',
                borderRadius:8, fontSize:13, marginTop:8 },
  alertOk:    { padding:'10px 14px', background:'#d1fae5', color:'#065f46',
                borderRadius:8, fontSize:13, marginTop:8 },
  btnPrimary: { background:'#1d4ed8', color:'#fff', border:'none', borderRadius:8,
                padding:'9px 20px', fontSize:14, fontWeight:600, cursor:'pointer' },
  btnSecondary:{ background:'#f3f4f6', color:'#374151', border:'1px solid #d1d5db',
                borderRadius:8, padding:'9px 16px', fontSize:14, cursor:'pointer' },
  btnDanger:  { background:'#fee2e2', color:'#991b1b', border:'1px solid #fca5a5',
                borderRadius:8, padding:'9px 16px', fontSize:14, cursor:'pointer' },
};
